import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {ControlEvents} from './control-events.js';
import {gameState} from '../state/GameState.js';
import {CameraShake} from '../utils/CameraShake.js';
import {vec3} from 'gl-matrix';
import {AxisX, AxisY} from '../utils/math.js';
import {PlayerStates} from '../state/Player.js';

const tempVec = vec3.create();
const tempVec2 = vec3.create();
const stateRotations: Record<string, number | null> = {
    up: 0,
    down: 180,
    left: 90,
    right: -90,
    upLeft: -45,
    upRight: 45,
    downLeft: -135,
    downRight: 135,
};

export class ThirdpersonCamera extends Component {
    static TypeName = 'thirdperson-camera';

    @property.float(100.0)
    speed!: number;
    @property.object()
    player!: Object3D;
    @property.float(-2)
    minYAngle!: number;
    @property.float(0)
    maxYAngle!: number;
    @property.float(4)
    cameraYOffset!: number;
    @property.float(5)
    cameraZOffset!: number;

    private mouseX: number = 0;
    private mouseY: number = 0;
    private moving: boolean = false;
    private rotation = new Float32Array(2);

    // Define shake parameters
    intensity = 0.5;
    duration = 0.5;
    shakeTime = 0;
    shakeOffset = [0, 0, 0];

    allowRotation = false;

    camera: Object3D | null = null;
    cameraShake = new CameraShake();

    private joystickPointerId: number | null = null;
    private cameraPointerId: number | null = null;

    start() {
        ControlEvents.onPointerDown.add(this.onPointerDown.bind(this));
        ControlEvents.onPointerMove.add(this.onPointerMove.bind(this));
        ControlEvents.onPointerUp.add(this.onPointerUp.bind(this));
        this.object.setPositionLocal([0, this.cameraYOffset, this.cameraZOffset]);
        this.updateView();
        ControlEvents.onStateChange.add(this.onStateChange.bind(this));
        ControlEvents.onJumpEnd.add(() => {
            // Trigger camera shake
            this.shake(0.01, 0.1);
        });
        gameState.onShake.add((intensity, duration) => {
            this.shake(intensity, duration);
        });

        this.camera = this.object.findByName('Camera')[0]!;
    }

    shake(intensity: number, duration: number) {
        this.cameraShake.strength = intensity;
        this.cameraShake.duration = duration;
        this.cameraShake.shake();
    }

    update(dt: number) {
        this.object.resetPositionRotation();

        this.player.getPositionWorld(tempVec);
        tempVec2[0] = 0;
        tempVec2[1] = this.cameraYOffset;
        tempVec2[2] = this.cameraZOffset;
        this.object.setPositionLocal(tempVec2);

        this.updateView(dt);
        this.object.translateLocal(tempVec);

        /* Make sure player is positioned below the center of the screen */
        tempVec[1] += 1.75;
        this.object.lookAt(tempVec);

        this.cameraShake.update(this.camera!, dt);
    }

    updateView(dt: number = 0) {
        // Calculate the new rotation angle
        const newRotationY = this.rotation[0] + this.mouseY * this.speed * dt * -1;

        // Clamp the rotation angle to the desired limits
        const minYAngle = this.minYAngle; // Minimum rotation angle
        const maxYAngle = this.maxYAngle; // Maximum rotation angle
        const clampedRotationY = Math.min(Math.max(newRotationY, minYAngle), maxYAngle);

        // Calculate lerped rotation around Y-axis
        const lerpedRotationY = this.lerp(this.rotation[0], clampedRotationY, 0.3); // Decrease lerp speed for faster rotation

        // Apply the rotation
        this.rotation[0] = lerpedRotationY;
        this.object.rotateAxisAngleRadLocal(AxisX, lerpedRotationY);

        // Rotate the player object around the Y-axis
        const deltaRotY = this.mouseX * this.speed * dt * -1;
        this.rotation[1] += deltaRotY;
        this.rotation[1] = this.rotation[1] % (Math.PI * 2); // Ensure rotation stays within 0 to 2*PI
        this.object.rotateAxisAngleRadLocal(AxisY, this.rotation[1]);
        this.player.rotateAxisAngleRadObject(AxisY, deltaRotY);

        if (!this.moving) {
            this.player.rotateAxisAngleRadObject(AxisY, this.mouseX * this.speed * dt);
        }

        // Reset mouse values after applying rotation
        this.mouseX = 0;
        this.mouseY = 0;
    }

    lookForward() {
        this.player.resetRotation();
        this.player.rotateAxisAngleDegObject([0, 1, 0], this.rotation[1]);
    }

    private prevPointerX: {[key: number]: number} = {};
    private prevPointerY: {[key: number]: number} = {};
    onPointerMove(event: PointerEvent) {
        const pointerId = event.pointerId;
        if (this.allowRotation && this.cameraPointerId === pointerId) {
            // Check if this pointer has a previous position
            if (
                this.prevPointerX.hasOwnProperty(pointerId) &&
                this.prevPointerY.hasOwnProperty(pointerId)
            ) {
                // Calculate movement relative to the previous pointer position
                const movementX = event.clientX - this.prevPointerX[pointerId];
                const movementY = event.clientY - this.prevPointerY[pointerId];

                // Update previous pointer position
                this.prevPointerX[pointerId] = event.clientX;
                this.prevPointerY[pointerId] = event.clientY;

                // Normalize pointer coordinates to be in the range [-1, 1]
                this.mouseX += (movementX / window.innerWidth) * 2;
                this.mouseY += (movementY / window.innerHeight) * 2;
            }
        }
    }

    onPointerDown(event: PointerEvent) {
        const pointerId = event.pointerId;
        // Assign action based on "Joystick UI is an element outside of the canvas"
        if (event.target !== this.engine.canvas) {
            if (this.joystickPointerId === null) {
                this.joystickPointerId = pointerId;
            }
        } else {
            if (this.cameraPointerId === null) {
                this.cameraPointerId = pointerId;

                // Store initial pointer position for camera actions
                this.prevPointerX[pointerId] = event.clientX;
                this.prevPointerY[pointerId] = event.clientY;
            }
        }
    }

    onPointerUp(event: PointerEvent) {
        const pointerId = event.pointerId;

        if (this.joystickPointerId === pointerId) {
            this.joystickPointerId = null;
        }

        if (this.cameraPointerId === pointerId) {
            this.cameraPointerId = null;
            delete this.prevPointerX[pointerId];
            delete this.prevPointerY[pointerId];
        }
    }

    lerp(start: number, end: number, speed: number) {
        return start + (end - start) * speed;
    }

    onStateChange(newState: string) {
        // Logic based on state change

        const rotation = stateRotations[newState];
        /* Allow rotation is controlled by the HUD:
         * - If false, the touch happened on the UI first and
         *   should not control the camera view.
         * - If true, the touch happened on area not covered by
         *   UI, so we're fine to rotate.
         */
        if (
            rotation !== null &&
            rotation !== undefined &&
            gameState.currentPlayer.state !== PlayerStates.Dead &&
            gameState.currentPlayer.state !== PlayerStates.Dying
        ) {
            this.moving = true;
            this.player.resetRotation();
            this.player.rotateAxisAngleRadObject(
                AxisY,
                (rotation * Math.PI) / 180 + this.rotation[1]
            );

            //TODO: Lerp instead of reset
        } else {
            this.moving = false;
        }
    }
}
